import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEnquetes } from '../hooks/useEnquetes';
import { 
  Search, 
  User, 
  FileText, 
  Settings, 
  LogOut,
  Globe,
  Shield
} from 'lucide-react';
import OSINTSearch from './OSINTSearch';
import OSINTMapping from './OSINTMapping';
import EnqueteManager from './EnqueteManager';
import ReportsManager from './ReportsManager';
import SettingsManager from './SettingsManager';
import ClientManager from './ClientManager';
import Administration from './Administration';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  // Si l'utilisateur est admin, commencer par le module Administration, sinon par les enquêtes
  const [activeTab, setActiveTab] = useState(user?.role === 'admin' ? 'administration' : 'enquetes');
  const [selectedEnquete, setSelectedEnquete] = useState<any>(null);
  
  // Utiliser le hook useEnquetes pour récupérer la liste des enquêtes
  const { enquetes } = useEnquetes();

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-osint-dark via-gray-900 to-osint-dark">
      {/* Header moderne avec glassmorphism */}
      <header className="bg-osint-gray/80 backdrop-blur-xl border-b border-osint-light-gray/30 sticky top-0 z-40">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center ml-2">
              <div className="w-40 h-20 flex items-center justify-center">
                <img 
                  src="/logo.png"
                  alt="Logo" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-6">
            {/* Indicateur de statut */}
            <div className="flex items-center space-x-2 status-indicator status-success">
              <span className="text-sm text-white font-medium">En ligne</span>
            </div>
            
            {/* Profil utilisateur */}
            <div className="flex items-center space-x-3 px-4 py-2 bg-osint-gray/60 rounded-xl backdrop-blur-sm border border-osint-border/50">
              <div className="text-right">
                <p className="text-sm text-white font-semibold">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-gray-400">{user?.email}</p>
              </div>
              <div className="w-8 h-8 bg-osint-gradient rounded-full flex items-center justify-center text-white font-bold text-sm">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
            </div>
            
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600/80 hover:bg-red-700 rounded-xl text-white text-sm transition-all duration-300 hover:shadow-lg hover:shadow-red-500/25 backdrop-blur-sm"
            >
              <LogOut className="w-4 h-4" />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex min-h-screen">
        {/* Sidebar moderne avec glassmorphism */}
        <aside className="w-64 sidebar-modern">
          <nav className="p-6">
            <div className="space-y-3">
              {/* Header sidebar */}
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Navigation</h3>
              </div>
              
              {/* Module Administration (visible uniquement pour les admins) */}
              {user?.role === 'admin' && (
                <button
                  onClick={() => setActiveTab('administration')}
                  className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl text-left transition-all duration-300 group ${
                    activeTab === 'administration' 
                      ? 'bg-red-600/20 text-white border border-red-500/30 shadow-lg shadow-red-500/10' 
                      : 'text-gray-300 hover:bg-red-600/10 hover:text-red-400 hover:border-red-500/20 border border-transparent'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${activeTab === 'administration' ? 'bg-red-500' : 'bg-red-500/20 group-hover:bg-red-500/30'}`}>
                    <Shield className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium">Administration</span>
                    <p className="text-xs text-gray-400">Gestion système</p>
                  </div>
                  <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full font-medium">
                    ADMIN
                  </span>
                </button>
              )}
              
              <button
                onClick={() => setActiveTab('enquetes')}
                className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl text-left transition-all duration-300 group ${
                  activeTab === 'enquetes' 
                    ? 'bg-osint-orange/20 text-white border border-osint-orange/30 shadow-lg shadow-osint-orange/10' 
                    : 'text-gray-300 hover:bg-osint-orange/10 hover:text-osint-orange hover:border-osint-orange/20 border border-transparent'
                }`}
              >
                <div className={`p-2 rounded-lg ${activeTab === 'enquetes' ? 'bg-osint-orange' : 'bg-osint-orange/20 group-hover:bg-osint-orange/30'}`}>
                  <FileText className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <span className="font-medium">Enquêtes</span>
                  <p className="text-xs text-gray-400">{enquetes.length} actives</p>
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('recherches')}
                className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl text-left transition-all duration-300 group ${
                  activeTab === 'recherches' 
                    ? 'bg-blue-600/20 text-white border border-blue-500/30 shadow-lg shadow-blue-500/10' 
                    : 'text-gray-300 hover:bg-blue-600/10 hover:text-blue-400 hover:border-blue-500/20 border border-transparent'
                }`}
              >
                <div className={`p-2 rounded-lg ${activeTab === 'recherches' ? 'bg-blue-500' : 'bg-blue-500/20 group-hover:bg-blue-500/30'}`}>
                  <Search className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <span className="font-medium">Recherches OSINT</span>
                  <p className="text-xs text-gray-400">Investigations</p>
                </div>
              </button>
               
              <button
                onClick={() => setActiveTab('cartographie')}
                className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl text-left transition-all duration-300 group ${
                  activeTab === 'cartographie' 
                    ? 'bg-green-600/20 text-white border border-green-500/30 shadow-lg shadow-green-500/10' 
                    : 'text-gray-300 hover:bg-green-600/10 hover:text-green-400 hover:border-green-500/20 border border-transparent'
                }`}
              >
                <div className={`p-2 rounded-lg ${activeTab === 'cartographie' ? 'bg-green-500' : 'bg-green-500/20 group-hover:bg-green-500/30'}`}>
                  <Globe className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <span className="font-medium">Cartographie</span>
                  <p className="text-xs text-gray-400">Géolocalisation</p>
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('clients')}
                className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl text-left transition-all duration-300 group ${
                  activeTab === 'clients' 
                    ? 'bg-purple-600/20 text-white border border-purple-500/30 shadow-lg shadow-purple-500/10' 
                    : 'text-gray-300 hover:bg-purple-600/10 hover:text-purple-400 hover:border-purple-500/20 border border-transparent'
                }`}
              >
                <div className={`p-2 rounded-lg ${activeTab === 'clients' ? 'bg-purple-500' : 'bg-purple-500/20 group-hover:bg-purple-500/30'}`}>
                  <User className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <span className="font-medium">Clients</span>
                  <p className="text-xs text-gray-400">Base de données</p>
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('rapports')}
                className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl text-left transition-all duration-300 group ${
                  activeTab === 'rapports' 
                    ? 'bg-yellow-600/20 text-white border border-yellow-500/30 shadow-lg shadow-yellow-500/10' 
                    : 'text-gray-300 hover:bg-yellow-600/10 hover:text-yellow-400 hover:border-yellow-500/20 border border-transparent'
                }`}
              >
                <div className={`p-2 rounded-lg ${activeTab === 'rapports' ? 'bg-yellow-500' : 'bg-yellow-500/20 group-hover:bg-yellow-500/30'}`}>
                  <FileText className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <span className="font-medium">Rapports</span>
                  <p className="text-xs text-gray-400">Exports PDF</p>
                </div>
              </button>
              
              <div className="mt-8 pt-6 border-t border-osint-border/30">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Système</h3>
                <button
                  onClick={() => setActiveTab('parametres')}
                  className={`w-full flex items-center space-x-3 px-4 py-4 rounded-xl text-left transition-all duration-300 group ${
                    activeTab === 'parametres' 
                      ? 'bg-gray-600/20 text-white border border-gray-500/30 shadow-lg shadow-gray-500/10' 
                      : 'text-gray-300 hover:bg-gray-600/10 hover:text-gray-300 hover:border-gray-500/20 border border-transparent'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${activeTab === 'parametres' ? 'bg-gray-500' : 'bg-gray-500/20 group-hover:bg-gray-500/30'}`}>
                    <Settings className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium">Paramètres</span>
                    <p className="text-xs text-gray-400">Configuration</p>
                  </div>
                </button>
              </div>
            </div>
          </nav>
        </aside>

        {/* Main Content avec animations */}
        <main className="flex-1 p-8 custom-scrollbar overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            {activeTab === 'administration' && user?.role === 'admin' && (
              <div className="slide-in">
                <Administration />
              </div>
            )}

            {activeTab === 'enquetes' && (
              <div className="slide-in">
                <EnqueteManager onEnqueteSelect={setSelectedEnquete} />
              </div>
            )}

            {activeTab === 'recherches' && (
              <div className="slide-in">
                <OSINTSearch />
              </div>
            )}

            {activeTab === 'cartographie' && (
              <div className="slide-in">
                <OSINTMapping />
              </div>
            )}

            {activeTab === 'clients' && (
              <div className="slide-in">
                <ClientManager />
              </div>
            )}

            {activeTab === 'rapports' && (
              <div className="slide-in">
                <ReportsManager enquete={selectedEnquete} enquetes={enquetes} />
              </div>
            )}

            {activeTab === 'parametres' && (
              <div className="slide-in">
                <SettingsManager />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard; 